import { useState, useEffect, useCallback, useRef } from 'react';
import { Scale, Wand2, Search, History, Loader2, Box, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast, Toaster } from 'sonner';
import { formatDate } from '@/utils/dateFormat';
import { productWeightsAPI, brandsAPI, categoriesAPI, vendorsAPI } from '@/services/api';

const ALL = '__all__';

// Inline numeric cell: saves on blur / Enter when the value actually changed.
const NumCell = ({ value, onSave, testId, placeholder = '—', width = 'w-20' }) => {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const commit = () => { const n = v === '' ? 0 : parseFloat(v); if (Number.isNaN(n)) return setV(value ?? ''); if ((value ?? 0) !== n) onSave(n); };
  return <Input type="number" min="0" step="any" value={v} placeholder={placeholder} onChange={e => setV(e.target.value)} onBlur={commit}
    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} className={`${width} h-8 text-right px-2`} data-testid={testId} />;
};

const WeightRow = ({ r, onSave, onAuto, onLog }) => {
  const dims = (f, n) => onSave(r.id, { [f]: n });
  return (
    <TableRow data-testid={`weight-row-${r.sku}`}>
      <TableCell className="py-1.5">
        <div className="flex items-center gap-2">
          {r.image_url ? <img src={r.image_url} alt="" className="w-9 h-9 rounded object-cover border bg-gray-50" onError={e => { e.currentTarget.style.visibility = 'hidden'; }} /> : <div className="w-9 h-9 rounded bg-gray-100 border" />}
          <div className="min-w-0">
            <div className="font-medium text-sm leading-tight truncate max-w-[320px]" title={r.name}>{r.name}</div>
            <div className="text-[11px] text-gray-500">{r.sku}{r.barcode ? ` · ${r.barcode}` : ''}{r.brand_name ? ` · ${r.brand_name}` : ''}</div>
          </div>
        </div>
      </TableCell>
      <TableCell className="py-1.5 text-xs text-gray-600">{r.category_name}<div className="text-[11px] text-gray-400 truncate max-w-[160px]">{r.supplier_names?.join(', ')}</div></TableCell>
      <TableCell className="py-1.5">
        <div className="flex items-center gap-1">
          <NumCell value={r.weight_grams} onSave={n => onSave(r.id, { weight_grams: n })} testId={`weight-input-${r.sku}`} width="w-24" />
          <span className="text-xs text-gray-500">g</span>
          {r.weight_source && <Badge variant="outline" className={`text-[10px] ${r.weight_source === 'auto' ? 'text-indigo-700 border-indigo-200' : 'text-gray-600'}`}>{r.weight_source}</Badge>}
        </div>
        {r.suggested_grams ? (
          <button onClick={() => onAuto(r.id)} className="mt-1 text-[11px] text-indigo-700 hover:underline inline-flex items-center gap-1" data-testid={`weight-auto-${r.sku}`}>
            <Wand2 className="w-3 h-3" /> {r.suggested_from} → {r.suggested_grams} g{r.weight_grams === r.suggested_grams ? ' ✓' : ''}
          </button>
        ) : <div className="mt-1 text-[11px] text-gray-400">no size in name</div>}
      </TableCell>
      <TableCell className="py-1.5">
        <div className="flex items-center gap-1">
          <NumCell value={r.length_cm} onSave={n => dims('length_cm', n)} testId={`len-input-${r.sku}`} placeholder="L" width="w-16" />
          <span className="text-gray-300">×</span>
          <NumCell value={r.width_cm} onSave={n => dims('width_cm', n)} testId={`wid-input-${r.sku}`} placeholder="W" width="w-16" />
          <span className="text-gray-300">×</span>
          <NumCell value={r.height_cm} onSave={n => dims('height_cm', n)} testId={`hgt-input-${r.sku}`} placeholder="H" width="w-16" />
          <span className="text-xs text-gray-500">cm</span>
        </div>
        {r.volumetric_grams ? <div className="mt-1 text-[11px] text-gray-500">Volumetric: {r.volumetric_grams} g (÷5000)</div> : null}
      </TableCell>
      <TableCell className="py-1.5 text-right">
        {r.shipping_grams ? (
          <div data-testid={`ship-${r.sku}`}>
            <div className="font-semibold">{r.shipping_grams} g</div>
            <Badge className={`text-[10px] ${r.shipping_basis === 'volumetric' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>{r.shipping_basis}</Badge>
          </div>
        ) : <span className="text-xs text-gray-400">—</span>}
      </TableCell>
      <TableCell className="py-1.5 text-[11px] text-gray-500">
        {r.weight_updated_by ? <div>{r.weight_updated_by}<div>{formatDate(r.weight_updated_at)}</div></div> : <div className="text-gray-400">not set yet</div>}
        <button onClick={() => onLog(r)} className="block text-indigo-600 hover:underline mt-1" data-testid={`weight-log-${r.sku}`}><History className="w-3 h-3 inline" /> log</button>
      </TableCell>
    </TableRow>
  );
};

export default function ProductWeightsPage() {
  const [data, setData] = useState({ rows: [], total: 0, pages: 1, stats: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', brand_id: ALL, category_id: ALL, supplier_id: ALL, status: 'all', page: 1 });
  const [opts, setOpts] = useState({ brands: [], categories: [], vendors: [] });
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoRes, setAutoRes] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [logFor, setLogFor] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [log, setLog] = useState([]);
  const debounce = useRef(null);

  const params = useCallback(() => ({
    search: filters.search || undefined, brand_id: filters.brand_id === ALL ? undefined : filters.brand_id,
    category_id: filters.category_id === ALL ? undefined : filters.category_id, supplier_id: filters.supplier_id === ALL ? undefined : filters.supplier_id,
    status: filters.status, page: filters.page, limit: 50,
  }), [filters]);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try { setData((await productWeightsAPI.list(params())).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => {
    Promise.all([brandsAPI.list({ limit: 1000 }), categoriesAPI.list({ limit: 1000 }), vendorsAPI.list({ limit: 1000 })])
      .then(([b, c, v]) => setOpts({ brands: b.data?.items || b.data || [], categories: c.data?.items || c.data || [], vendors: v.data?.items || v.data || [] }))
      .catch(() => {});
  }, []);
  useEffect(() => { clearTimeout(debounce.current); debounce.current = setTimeout(() => load(), filters.search ? 350 : 0); return () => clearTimeout(debounce.current); }, [load, filters.search]);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v, page: k === 'page' ? v : 1 }));
  const patchRow = (row) => setData(d => ({ ...d, rows: d.rows.map(r => r.id === row.id ? { ...r, ...row, brand_name: r.brand_name, category_name: r.category_name, supplier_names: r.supplier_names } : r) }));

  const save = async (id, changes) => {
    try { const { data: res } = await productWeightsAPI.update(id, changes); patchRow(res.row); if (Object.keys(res.changed).length) { toast.success('Saved'); load(true); } }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); load(); }
  };
  const auto = async (id) => {
    try { const { data: res } = await productWeightsAPI.auto(id); patchRow(res.row); toast.success(`Weight set to ${res.suggestion.grams} g from "${res.suggestion.token}"`); load(true); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not auto-detect'); }
  };
  const runAutoFill = async () => {
    setAutoBusy(true);
    try { const { data: res } = await productWeightsAPI.autoFill({ ...params(), status: undefined, page: undefined, limit: undefined }); setAutoRes(res); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Auto-fill failed'); }
    finally { setAutoBusy(false); }
  };
  const openLog = async (r) => { setLogFor(r); setLog([]); setLogOpen(true); setLog((await productWeightsAPI.log(r ? { product_id: r.id, limit: 100 } : { limit: 200 })).data); };

  const s = data.stats || {};
  return (
    <div className="p-6 space-y-4" data-testid="product-weights-page">
      <Toaster position="top-right" richColors />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Scale className="w-6 h-6" /> Products Weights</h1>
          <p className="text-sm text-gray-500">Weight in grams from the product name (round down to 10 g, +20 g packing) · L×W×H in cm → volumetric ÷5000 · shipment uses the higher one. Shared by all stores.</p>
        </div>
        <div className="flex gap-2 mr-14">
          <Button variant="outline" onClick={() => openLog(null)} data-testid="weights-log-all"><History className="w-4 h-4 mr-1" /> Edit log</Button>
          <Button variant="outline" onClick={load} data-testid="weights-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setAutoRes(null); setAutoOpen(true); }} disabled={!s.auto_fillable} data-testid="weights-autofill-open">
            <Wand2 className="w-4 h-4 mr-1" /> Auto-fill blanks ({s.auto_fillable || 0})
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[['Products', s.total, 'all'], ['Missing weight', s.missing, 'missing'], ['Auto-fillable from name', s.auto_fillable, 'suggested'], ['With dimensions', s.with_dims, 'volumetric']].map(([t, v, st]) => (
          <button key={st} onClick={() => set('status', st)} className={`rounded-lg border p-3 text-left bg-white hover:border-indigo-300 ${filters.status === st ? 'border-indigo-500 ring-1 ring-indigo-200' : ''}`} data-testid={`weights-stat-${st}`}>
            <div className="text-[11px] text-gray-500">{t}</div><div className="text-xl font-bold">{v ?? '—'}</div>
          </button>
        ))}
      </div>

      <Card><CardContent className="p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
          <Input value={filters.search} onChange={e => set('search', e.target.value)} placeholder="Search name / barcode / SKU" className="pl-8" data-testid="weights-search" />
        </div>
        <Select value={filters.brand_id} onValueChange={v => set('brand_id', v)}><SelectTrigger className="w-44" data-testid="weights-brand"><SelectValue placeholder="Brand" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All brands</SelectItem>{opts.brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
        <Select value={filters.category_id} onValueChange={v => set('category_id', v)}><SelectTrigger className="w-44" data-testid="weights-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All categories</SelectItem>{opts.categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
        <Select value={filters.supplier_id} onValueChange={v => set('supplier_id', v)}><SelectTrigger className="w-44" data-testid="weights-supplier"><SelectValue placeholder="Supplier" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All suppliers</SelectItem>{opts.vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select>
        <Select value={filters.status} onValueChange={v => set('status', v)}><SelectTrigger className="w-40" data-testid="weights-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="missing">Missing weight</SelectItem><SelectItem value="suggested">Auto-fillable</SelectItem><SelectItem value="has">Has weight</SelectItem><SelectItem value="volumetric">Has dimensions</SelectItem></SelectContent></Select>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Category / Supplier</TableHead><TableHead>Weight (g)</TableHead><TableHead>Dimensions L × W × H (cm)</TableHead><TableHead className="text-right">Shipping weight</TableHead><TableHead>Updated by</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading && !data.rows.length && <TableRow><TableCell colSpan={6} className="text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></TableCell></TableRow>}
            {!loading && !data.rows.length && <TableRow><TableCell colSpan={6} className="text-center py-10 text-gray-400" data-testid="weights-empty"><Box className="w-6 h-6 mx-auto mb-1" />No products match</TableCell></TableRow>}
            {data.rows.map(r => <WeightRow key={r.id} r={r} onSave={save} onAuto={auto} onLog={openLog} />)}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between p-3 text-sm text-gray-600 border-t">
          <span data-testid="weights-count">{data.total} products · page {data.page || 1} of {data.pages}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => set('page', filters.page - 1)} data-testid="weights-prev">Prev</Button>
            <Button size="sm" variant="outline" disabled={filters.page >= data.pages} onClick={() => set('page', filters.page + 1)} data-testid="weights-next">Next</Button>
          </div>
        </div>
      </CardContent></Card>

      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="max-w-lg" data-testid="weights-autofill-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-indigo-600" /> Auto-fill weights from product names</DialogTitle></DialogHeader>
          {!autoRes ? (
            <div className="text-sm space-y-2">
              <p>Only products with a <b>blank weight</b> in the current filter are updated; weights already entered (manual or auto) are left untouched.</p>
              <p className="text-gray-600">Rule: size in name (g / gm / ml / kg / L…) → grams, rounded down to the nearest 10, plus 20 g. e.g. 236ML → 250 g, 150ml → 170 g, 0.500 kg → 520 g.</p>
              <p className="font-medium">{s.auto_fillable || 0} product(s) will be updated.</p>
            </div>
          ) : (
            <div className="text-sm space-y-2">
              <div className="flex gap-3"><Badge className="bg-emerald-100 text-emerald-800">Updated {autoRes.updated}</Badge><Badge variant="outline">Scanned {autoRes.scanned}</Badge><Badge variant="outline">No size in name {autoRes.no_size_in_name}</Badge></div>
              <div className="max-h-64 overflow-auto border rounded" data-testid="weights-autofill-result">
                {autoRes.items.map(i => <div key={i.id} className="flex justify-between px-2 py-1 border-b text-xs"><span className="truncate max-w-[300px]">{i.name}</span><span className="text-indigo-700">{i.from} → {i.grams} g</span></div>)}
                {!autoRes.items.length && <div className="p-3 text-gray-400 text-xs">Nothing to update.</div>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoOpen(false)} data-testid="weights-autofill-close">{autoRes ? 'Close' : 'Cancel'}</Button>
            {!autoRes && <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={runAutoFill} disabled={autoBusy} data-testid="weights-autofill-run">{autoBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Update {s.auto_fillable || 0} products</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-2xl" data-testid="weights-log-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" /> {logFor ? `Edit log — ${logFor.name}` : 'Recent weight edits'}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            {!log.length && <div className="text-sm text-gray-400 p-4 text-center">No edits yet.</div>}
            {log.map(l => (
              <div key={l.id} className="border-b py-2 text-xs" data-testid="weights-log-row">
                <div className="flex justify-between"><span className="font-medium">{logFor ? '' : `${l.product_name} · `}<Badge variant="outline" className="text-[10px]">{l.mode}</Badge> {l.note}</span><span className="text-gray-500">{l.by} · {formatDate(l.at)}</span></div>
                <div className="text-gray-600">{Object.entries(l.changes || {}).map(([k, c]) => `${k.replace('_cm', ' cm').replace('_grams', ' g')}: ${c.from} → ${c.to}`).join(' · ')}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
